import { createBrowserRouter, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import AppShell from './components/AppShell';
import CustomerPortalLayout from './components/CustomerPortalLayout';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import SupportLoginPage from './pages/SupportLoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import CustomerPortalPage from './pages/CustomerPortalPage';
import SubscriptionsPage from './pages/SubscriptionsPage';
import { useAuth } from './hooks/useAuth';
import { useModuleAccess } from './hooks/useModuleAccess';
import { ProtectedRoute, RequireResetRoute } from './components/RouteGuards';
import LoadingSpinner from './components/LoadingSpinner';
import RouteError from './components/RouteError';

const HomeDashboard = lazy(() => import('./pages/HomeDashboard'));
const TasksPage = lazy(() => import('./pages/TasksPage'));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage'));
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
const FinancialsPage = lazy(() => import('./pages/FinancialsPage'));
const MemoryPage = lazy(() => import('./pages/MemoryPage'));
const WorkspacePeoplePage = lazy(() => import('./pages/WorkspacePeoplePage'));
const KnowledgeBasePage = lazy(() => import('./pages/KnowledgeBasePage'));
const KnowledgeGraphPage = lazy(() => import('./pages/KnowledgeGraphPage'));
const SettingsWorkspaceAdmin = lazy(() => import('./pages/SettingsWorkspaceAdmin'));
const SettingsAPIKeys = lazy(() => import('./pages/SettingsAPIKeys'));
const SettingsAIUsagePage = lazy(() => import('./pages/SettingsAIUsagePage'));
const SettingsIntegrations = lazy(() => import('./pages/SettingsIntegrations'));
const SettingsAffiliateLinks = lazy(() => import('./pages/SettingsAffiliateLinks'));
const SettingsSubscriptions = lazy(() => import('./pages/SettingsSubscriptions'));
const SettingsVirtualAssistant = lazy(() => import('./pages/SettingsVirtualAssistant'));
const SettingsAssistantProtocol = lazy(() => import('./pages/SettingsAssistantProtocol'));
const UserSitesPage = lazy(() => import('./pages/UserSitesPage'));
const RemoteAgentsPage = lazy(() => import('./pages/RemoteAgentsPage'));
const RemoteAgentDetailPage = lazy(() => import('./pages/RemoteAgentDetailPage'));
const SettingsWhatsApp = lazy(() => import('./pages/SettingsWhatsApp'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const AIChatPage = lazy(() => import('./pages/AIChatPage'));
// Messenger module (owner spec 2026-10-19): owner ↔ paired remote agents.
const MessengerPage = lazy(() => import('./pages/MessengerPage'));
// Owner 2026-10-19: Messenger and the app chat are ONE module — the hub hosts both tabs.
const MessengerHubPage = lazy(() => import('./pages/MessengerHubPage'));
const AIProviderSettings = lazy(() => import('./pages/AIProviderSettings'));
const AIGatewayPage = lazy(() => import('./pages/portal/AIGatewayPage'));
const MastermindAudit = lazy(() => import('./pages/MastermindAudit'));

// User Agents platform (Phase 1+2)
const AgentsPage = lazy(() => import('./pages/AgentsPage'));
const AutonomyLeaderboardPage = lazy(() => import('./pages/AutonomyLeaderboardPage'));
const UserAgentsAdmin = lazy(() => import('./pages/portal/UserAgentsAdmin'));
// Prompts Command Center (owner directive 2026-10-08)
const PromptsCommandCenter = lazy(() => import('./pages/PromptsCommandCenter'));

// JobBot
const JobBotDashboard = lazy(() => import('./pages/JobBotDashboard'));
const JobBotLeads = lazy(() => import('./pages/JobBotLeads'));
const JobBotApplications = lazy(() => import('./pages/JobBotApplications'));
const JobBotResumes = lazy(() => import('./pages/JobBotResumes'));
const JobBotProfile = lazy(() => import('./pages/JobBotProfile'));
const JobBotSearches = lazy(() => import('./pages/JobBotSearches'));
const JobSearchDetail = lazy(() => import('./pages/JobSearchDetail'));
const JobBotInterviews = lazy(() => import('./pages/JobBotInterviews'));

// Trading
const TradingDashboard = lazy(() => import('./pages/TradingDashboard'));
const TradingOrders = lazy(() => import('./pages/TradingOrders'));
const TradingPositions = lazy(() => import('./pages/TradingPositions'));
const TradingHistory = lazy(() => import('./pages/TradingHistory'));
const TradingPerformance = lazy(() => import('./pages/TradingPerformance'));

// Social
const SocialManager = lazy(() => import('./pages/SocialManager'));
const SocialAccounts = lazy(() => import('./pages/SocialAccounts'));
const SocialContent = lazy(() => import('./pages/SocialContent'));
const SocialComposer = lazy(() => import('./pages/SocialComposer'));
const SocialAnalytics = lazy(() => import('./pages/SocialAnalytics'));

// Content
const ContentStudio = lazy(() => import('./pages/ContentStudio'));
const ContentTimeline = lazy(() => import('./pages/ContentTimeline'));
const ContentProjects = lazy(() => import('./pages/ContentProjects'));
const ContentJobs = lazy(() => import('./pages/ContentJobs'));
const ContentAssets = lazy(() => import('./pages/ContentAssets'));

// Marketplace
const MarketplaceAccounts = lazy(() => import('./pages/MarketplaceAccounts'));
const SalesDashboard = lazy(() => import('./pages/SalesDashboard'));

// Operations
const OpsAutomation = lazy(() => import('./pages/OpsAutomation'));

// Email
const EmailAccountsPage = lazy(() => import('./pages/EmailAccountsPage'));
const EmailInboxPage = lazy(() => import('./pages/EmailInboxPage'));
const EmailCleanupPage = lazy(() => import('./pages/EmailCleanupPage'));
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const FreeTierAdmin = lazy(() => import('./pages/FreeTierAdmin'));
const SettingsAccount = lazy(() => import('./pages/SettingsAccount'));
const PrudentialCredentialsPage = lazy(() => import('./pages/PrudentialCredentialsPage'));
const SettingsEmail = lazy(() => import('./pages/SettingsEmail'));

// Roadmap
const RoadmapSpec = lazy(() => import('./pages/RoadmapSpec'));

// Marketing
const MarketingManager = lazy(() => import('./pages/MarketingManager'));

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="flex h-full items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

function Lazy({ children }: { children: React.ReactNode }) {
  return <SuspenseWrapper>{children}</SuspenseWrapper>;
}

function ModuleRoute({ module, children }: { module: string; children: React.ReactNode }) {
  const { enabled, loading } = useModuleAccess();
  if (loading) return <LoadingSpinner size="lg" />;
  return enabled(module) ? <>{children}</> : <Navigate to="/app" replace />;
}

function OwnerRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  // Auth is restored from localStorage in an effect, so on a cold load (or a
  // deep link / hard refresh) the first paint has user=null while `loading` is
  // still true. Checking is_owner before the restore finishes bounced owner
  // pages (/app/admin/users, /app/admin/free-tier) straight to /app. Wait it out.
  if (loading) return <LoadingSpinner size="lg" />;
  if (!user?.is_owner) return <Navigate to="/app" replace />;
  return <>{children}</>;
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <LandingPage />,
    errorElement: <RouteError />,
  },
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/register',
    element: <RegisterPage />,
  },
  {
    path: '/support-login',
    element: <SupportLoginPage />,
  },
  {
    path: '/reset-password',
    element: (
      <RequireResetRoute>
        <ResetPasswordPage />
      </RequireResetRoute>
    ),
  },
  // Public legal pages — intentionally unauthenticated.
  {
    path: '/privacy',
    element: <PrivacyPolicyPage />,
  },
  {
    path: '/terms',
    element: <TermsOfServicePage />,
  },
  {
    path: '/app',
    element: (
      <ProtectedRoute>
        <AppShell />
      </ProtectedRoute>
    ),
    // Any route crash under /app (a lazy chunk that failed to import, a render
    // error in a page) shows the friendly RouteError instead of the developer
    // screen — and auto-reloads once on a stale-deploy chunk mismatch.
    errorElement: <RouteError />,
    children: [
      { index: true, element: <Lazy><HomeDashboard /></Lazy> },
      { path: 'chat', element: <Navigate to="/app/messenger" replace /> },
      { path: 'messenger', element: <Lazy><MessengerHubPage /></Lazy> },
      // User Agents (user-facing)
      { path: 'agents', element: <Lazy><AgentsPage /></Lazy> },
      { path: 'leaderboard', element: <Lazy><AutonomyLeaderboardPage /></Lazy> },
      { path: 'prompts', element: <OwnerRoute><Lazy><PromptsCommandCenter /></Lazy></OwnerRoute> },
      { path: 'tasks', element: <Lazy><TasksPage /></Lazy> },
      { path: 'projects', element: <Lazy><ProjectsPage /></Lazy> },
      { path: 'calendar', element: <Lazy><CalendarPage /></Lazy> },
      { path: 'financials', element: <Lazy><FinancialsPage /></Lazy> },
      { path: 'memory', element: <Lazy><MemoryPage /></Lazy> },
      { path: 'files', element: <Lazy><ContentAssets /></Lazy> },
      { path: 'sites', element: <Lazy><UserSitesPage /></Lazy> },
      { path: 'people', element: <Navigate to="/app/customer-portal/customers" replace /> },
      { path: 'knowledge-base', element: <Lazy><KnowledgeBasePage /></Lazy> },
      { path: 'graphs', element: <Lazy><KnowledgeGraphPage /></Lazy> },
      // JobBot
      { path: 'jobbot', element: <ModuleRoute module="jobbot"><Lazy><JobBotDashboard /></Lazy></ModuleRoute> },
      { path: 'jobbot/sites', element: <Navigate to="/app/customer-portal/jobsites" replace /> },
      { path: 'jobbot/leads', element: <ModuleRoute module="jobbot"><Lazy><JobBotLeads /></Lazy></ModuleRoute> },
      { path: 'jobbot/applications', element: <ModuleRoute module="jobbot"><Lazy><JobBotApplications /></Lazy></ModuleRoute> },
      { path: 'jobbot/resumes', element: <ModuleRoute module="jobbot"><Lazy><JobBotResumes /></Lazy></ModuleRoute> },
      { path: 'jobbot/profile', element: <ModuleRoute module="jobbot"><Lazy><JobBotProfile /></Lazy></ModuleRoute> },
      { path: 'jobbot/searches', element: <ModuleRoute module="jobbot"><Lazy><JobBotSearches /></Lazy></ModuleRoute> },
      { path: 'jobbot/searches/:searchId', element: <ModuleRoute module="jobbot"><Lazy><JobSearchDetail /></Lazy></ModuleRoute> },
      { path: 'jobbot/interviews', element: <ModuleRoute module="jobbot"><Lazy><JobBotInterviews /></Lazy></ModuleRoute> },
      // Trading
      { path: 'trading', element: <ModuleRoute module="trading"><Lazy><TradingDashboard /></Lazy></ModuleRoute> },
      { path: 'trading/orders', element: <ModuleRoute module="trading"><Lazy><TradingOrders /></Lazy></ModuleRoute> },
      { path: 'trading/positions', element: <ModuleRoute module="trading"><Lazy><TradingPositions /></Lazy></ModuleRoute> },
      { path: 'trading/history', element: <ModuleRoute module="trading"><Lazy><TradingHistory /></Lazy></ModuleRoute> },
      { path: 'trading/performance', element: <ModuleRoute module="trading"><Lazy><TradingPerformance /></Lazy></ModuleRoute> },
      // Social
      { path: 'social', element: <ModuleRoute module="social"><Lazy><SocialManager /></Lazy></ModuleRoute> },
      { path: 'social/accounts', element: <ModuleRoute module="social"><Lazy><SocialAccounts /></Lazy></ModuleRoute> },
      { path: 'social/content', element: <ModuleRoute module="social"><Lazy><SocialContent /></Lazy></ModuleRoute> },
      { path: 'social/composer', element: <ModuleRoute module="social"><Lazy><SocialComposer /></Lazy></ModuleRoute> },
      { path: 'social/analytics', element: <ModuleRoute module="social"><Lazy><SocialAnalytics /></Lazy></ModuleRoute> },
      // Content
      { path: 'content', element: <ModuleRoute module="content"><Lazy><ContentStudio /></Lazy></ModuleRoute> },
      { path: 'content/timeline', element: <ModuleRoute module="content"><Lazy><ContentTimeline /></Lazy></ModuleRoute> },
      { path: 'content/timeline/:runId', element: <ModuleRoute module="content"><Lazy><ContentTimeline /></Lazy></ModuleRoute> },
      { path: 'content/projects', element: <ModuleRoute module="content"><Lazy><ContentProjects /></Lazy></ModuleRoute> },
      { path: 'content/jobs', element: <ModuleRoute module="content"><Lazy><ContentJobs /></Lazy></ModuleRoute> },
      { path: 'content/assets', element: <ModuleRoute module="content"><Lazy><ContentAssets /></Lazy></ModuleRoute> },
      // Marketplace
      { path: 'marketplace', element: <ModuleRoute module="marketplace"><Lazy><MarketplaceAccounts /></Lazy></ModuleRoute> },
      { path: 'marketplace/sales', element: <ModuleRoute module="marketplace"><Lazy><SalesDashboard /></Lazy></ModuleRoute> },
      // Operations
      { path: 'opportunity-scanner', element: <ModuleRoute module="automation"><Lazy><OpsAutomation view="opportunities" /></Lazy></ModuleRoute> },
      { path: 'ops/automation', element: <ModuleRoute module="automation"><Lazy><OpsAutomation /></Lazy></ModuleRoute> },
      { path: 'mastermind', element: <ModuleRoute module="automation"><Lazy><MastermindAudit /></Lazy></ModuleRoute> },
      // Marketing
      { path: 'marketing', element: <ModuleRoute module="marketing"><Lazy><MarketingManager /></Lazy></ModuleRoute> },
      // Roadmap
      { path: 'roadmap', element: <ModuleRoute module="roadmap"><Lazy><RoadmapSpec /></Lazy></ModuleRoute> },
      // Email
      { path: 'email', element: <Navigate to="/app/email/inbox" replace /> },
      { path: 'email/inbox', element: <Lazy><EmailInboxPage /></Lazy> },
      { path: 'email/cleanup', element: <Lazy><EmailCleanupPage /></Lazy> },
      { path: 'email/accounts', element: <Lazy><EmailAccountsPage /></Lazy> },
      // Admin
      { path: 'admin/users', element: <Navigate to="/app/customer-portal/users" replace /> },
      { path: 'admin/free-tier', element: <OwnerRoute><Lazy><FreeTierAdmin /></Lazy></OwnerRoute> },
      // Settings
      { path: 'settings/account', element: <Navigate to="/app/profile" replace /> },
      { path: 'settings/prudential', element: <Navigate to="/app/settings/credentials" replace /> },
      { path: 'settings/credentials', element: <Lazy><PrudentialCredentialsPage /></Lazy> },
      { path: 'settings/email', element: <Lazy><SettingsEmail /></Lazy> },
      { path: 'settings/workspace', element: <Lazy><SettingsWorkspaceAdmin /></Lazy> },
      { path: 'settings/ai-providers', element: <Lazy><AIGatewayPage /></Lazy> },
      { path: 'settings/api-usage', element: <Lazy><AIGatewayPage /></Lazy> },
      { path: 'settings/integrations', element: <Lazy><AIGatewayPage /></Lazy> },
      { path: 'settings/affiliate-links', element: <Lazy><SettingsAffiliateLinks /></Lazy> },
      { path: 'settings/subscriptions', element: <Lazy><SubscriptionsPage /></Lazy> },
      { path: 'settings/billing', element: <Lazy><SubscriptionsPage /></Lazy> },
      { path: 'settings/stripe', element: <Navigate to="/app/customer-portal" replace /> },
      { path: 'settings/virtual-assistant', element: <Lazy><SettingsVirtualAssistant /></Lazy> },
      { path: 'settings/assistant-protocol', element: <Lazy><SettingsAssistantProtocol /></Lazy> },
      { path: 'settings/whatsapp', element: <Lazy><SettingsWhatsApp /></Lazy> },
      { path: 'settings/remote-agents', element: <Lazy><RemoteAgentsPage /></Lazy> },
      { path: 'settings/remote-agents/:id', element: <Lazy><RemoteAgentDetailPage /></Lazy> },
      // Short alias (2026-10-20) — same page, friendlier URL for shares.
      { path: 'remote-agents/:id', element: <Lazy><RemoteAgentDetailPage /></Lazy> },
      { path: 'profile', element: <Lazy><UserProfile /></Lazy> },
    ],
  },
  // Customer Portal — its OWN full-screen layout (owner directive 2026-09-30).
  // Rendered outside AppShell so the site sidebar/topbar never squeeze it. The
  // URL /app/customer-portal* is unchanged, so existing links keep working.
  {
    path: '/app/customer-portal',
    element: (
      <ProtectedRoute>
        <CustomerPortalLayout />
      </ProtectedRoute>
    ),
    errorElement: <RouteError />,
    children: [
      { index: true, element: <Lazy><CustomerPortalPage /></Lazy> },
      { path: 'users', element: <Lazy><CustomerPortalPage initialTab="users" /></Lazy> },
      { path: 'customers', element: <Lazy><CustomerPortalPage initialTab="customers" /></Lazy> },
      { path: 'jobsites', element: <Lazy><CustomerPortalPage initialTab="jobsites" /></Lazy> },
      { path: 'roles', element: <Lazy><CustomerPortalPage initialTab="roles" /></Lazy> },
      { path: 'user-agents', element: <OwnerRoute><Lazy><UserAgentsAdmin /></Lazy></OwnerRoute> },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
    errorElement: <RouteError />,
  },
]);
